/**
 * Shim for `@react-navigation/native`.
 *
 * Provides an in-memory navigation model (contexts, hooks, actions and the
 * container) that the stack / top-tab navigator shims build on. An in-memory
 * stack is used rather than URL routing because the app passes non-serializable
 * params through navigation (matching the original React Navigation behaviour).
 */
import React, { createContext, useContext, useEffect } from 'react';

/* ------------------------------ Types ------------------------------------ */

export type ParamListBase = Record<string, object | undefined>;

export interface Route<Name extends string = string, Params = object | undefined> {
  key: string;
  name: Name;
  params?: Params;
}

export type RouteProp<ParamList = ParamListBase, RouteName extends keyof ParamList = string & keyof ParamList> = {
  key: string;
  name: RouteName;
  params: ParamList[RouteName];
};

export interface NavigationAction {
  type: string;
  payload?: any;
}

export interface Navigation {
  navigate: (name: string, params?: object) => void;
  push: (name: string, params?: object) => void;
  replace: (name: string, params?: object) => void;
  goBack: () => void;
  canGoBack: () => boolean;
  popToTop: () => void;
  pop: (count?: number) => void;
  reset: (state: { index: number; routes: { name: string; params?: object }[] }) => void;
  setParams: (params: object) => void;
  setOptions: (options: object) => void;
  dispatch: (action: NavigationAction | ((state: any) => NavigationAction)) => void;
  addListener: (type: string, callback: (e?: any) => void) => () => void;
  removeListener: (type: string, callback: (e?: any) => void) => void;
  isFocused: () => boolean;
  getParent: () => Navigation | undefined;
  getState: () => any;
}

/* ----------------------------- Contexts ---------------------------------- */

export const NavigationContext = createContext<Navigation | undefined>(undefined);
export const RouteContext = createContext<Route | undefined>(undefined);
/** True when the nearest enclosing scene is currently focused (top of stack / active tab). */
export const FocusContext = createContext<boolean>(true);

/* ------------------------------- Hooks ----------------------------------- */

export function useNavigation<T = Navigation>(): T {
  const navigation = useContext(NavigationContext);
  if (!navigation) {
    throw new Error("useNavigation must be used within a navigator ('NavigationContainer').");
  }
  return navigation as unknown as T;
}

export function useRoute<T extends Route = Route>(): T {
  const route = useContext(RouteContext);
  return (route ?? { key: 'unknown', name: 'unknown', params: {} }) as T;
}

export function useIsFocused(): boolean {
  return useContext(FocusContext);
}

export function useFocusEffect(effect: () => void | (() => void)): void {
  const isFocused = useContext(FocusContext);
  useEffect(() => {
    if (!isFocused) return;
    const cleanup = effect();
    return typeof cleanup === 'function' ? cleanup : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);
}

export function useNavigationState<T = any>(selector: (state: any) => T): T {
  const navigation = useContext(NavigationContext);
  return selector(navigation?.getState?.() ?? {});
}

export function useScrollToTop(): void {
  /* no-op on web */
}

/* ------------------------------ Actions ---------------------------------- */

export const StackActions = {
  push: (name: string, params?: object): NavigationAction => ({ type: 'PUSH', payload: { name, params } }),
  pop: (count = 1): NavigationAction => ({ type: 'POP', payload: { count } }),
  popToTop: (): NavigationAction => ({ type: 'POP_TO_TOP' }),
  replace: (name: string, params?: object): NavigationAction => ({ type: 'REPLACE', payload: { name, params } }),
};

export const CommonActions = {
  navigate: (name: string, params?: object): NavigationAction => ({ type: 'NAVIGATE', payload: { name, params } }),
  goBack: (): NavigationAction => ({ type: 'GO_BACK' }),
  reset: (state: any): NavigationAction => ({ type: 'RESET', payload: state }),
  setParams: (params: object): NavigationAction => ({ type: 'SET_PARAMS', payload: { params } }),
};

export const TabActions = {
  jumpTo: (name: string, params?: object): NavigationAction => ({ type: 'JUMP_TO', payload: { name, params } }),
};

export const DrawerActions = {
  openDrawer: (): NavigationAction => ({ type: 'OPEN_DRAWER' }),
  closeDrawer: (): NavigationAction => ({ type: 'CLOSE_DRAWER' }),
  toggleDrawer: (): NavigationAction => ({ type: 'TOGGLE_DRAWER' }),
};

/* --------------------------- Container ----------------------------------- */

export interface NavigationContainerProps {
  children?: React.ReactNode;
  ref?: React.Ref<any>;
  onReady?: () => void;
  onStateChange?: (state: any) => void;
  theme?: any;
  linking?: any;
  fallback?: React.ReactNode;
}

export const NavigationContainer = React.forwardRef<any, NavigationContainerProps>(
  function NavigationContainer({ children, onReady }, ref) {
    useEffect(() => {
      onReady?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useImperativeHandle(ref, () => ({
      getCurrentRoute: () => undefined,
      getRootState: () => ({}),
      navigate: () => {},
    }));
    return <>{children}</>;
  },
);

export const DefaultTheme = { dark: false, colors: {} as Record<string, string> };
export const DarkTheme = { dark: true, colors: {} as Record<string, string> };

export function useLinkTo() {
  return (_: string) => {};
}
export function useLinkProps() {
  return { onPress: () => {}, href: '#' };
}
export const CommonActionsDefault = CommonActions;

export default {
  NavigationContainer,
  NavigationContext,
  RouteContext,
  useNavigation,
  useRoute,
  useIsFocused,
  useFocusEffect,
  StackActions,
  CommonActions,
  TabActions,
  DefaultTheme,
  DarkTheme,
};
